// The bell's Details (ART-328, product owner R6): an opportunity invite — or a show request (ART-327) — in the
// notification centre opens the SAME Details view the Opportunities card does — a pop-up on desktop, a sheet on the phone — where the answer lives.
//
// The bell opens from the topbar, OUTSIDE any band, and the row is about the band IT names, not whichever one is
// mounted — so this mounts its own BandProvider for that band, from the band's own navigation document (the
// server's capabilities, never a guess). A row heard through an org seat needs that org's lens, which only the
// roster shell can supply; anywhere else such a row falls back to its link.
import { useEffect, useMemo } from "react";
import { Spinner } from "@cntrl/ui";
import { BandProvider, bandContextFromNav, useBandContextOrNull } from "../../app/bandContext.js";
import { useNavigation } from "../../app/hooks/useSession.js";
import { orgLensContext } from "../../app/hooks/useRoster.js";
import { getOrgLens } from "../../app/orgLens.js";
import type { NotificationRow } from "../../app/hooks/useNotifications.js";
import { CalendarEventDetails, type OpenCalendarEvent } from "../booking/CalendarEventDetails.js";
import { ShowRequestDetails } from "../booking/ShowRequestDetails.js";
import { usePhoneLayout } from "../../app/phoneLayout.js";

/** The navigation context a row's Details can be read under, or null when none can be from here. */
export function detailsContextFor(row: NotificationRow): string | null {
  if (!row.bandId) return null;
  // Under the roster shell the reader is acting through their org, whatever the row carries: a row heard as a band
  // member names no org, and a manager holds no band seat, so `band:{id}` would be refused (ART-330). The lens's
  // own document is the one to read it under.
  const lens = getOrgLens();
  if (lens) return orgLensContext(lens, row.bandId);
  if (row.orgId) return null;
  return `band:${row.bandId}`;
}

export function NotificationDetails({ row, open, offerId, onClose, onFallback }: {
  row: NotificationRow;
  /** An invite's Details (ART-328)… */
  open?: OpenCalendarEvent;
  /** …or a show request's (ART-327). */
  offerId?: string;
  onClose: () => void;
  /** The row's link — used when this band cannot be opened from here. */
  onFallback: () => void;
}) {
  const mounted = useBandContextOrNull();
  const desktop = !usePhoneLayout();
  const reuse = !!mounted && mounted.bandId === row.bandId && (mounted.orgId ?? null) === (row.orgId ?? null);
  const context = reuse ? undefined : (detailsContextFor(row) ?? undefined);
  const nav = useNavigation(context);
  const value = useMemo(() => {
    if (!nav.data) return null;
    // The sheet-vs-pop-up choice is the shell's (`usePhoneLayout`), not this context's `shell`.
    return bandContextFromNav(nav.data, getOrgLens() ? "roster" : desktop ? "artist" : "mobile");
  }, [nav.data, desktop]);

  const failed = !reuse && (nav.isError || (!!nav.data && !value));
  useEffect(() => {
    if (failed) onFallback();
  }, [failed, onFallback]);

  const details = offerId
    ? <ShowRequestDetails offerId={offerId} onClose={onClose} />
    : open ? <CalendarEventDetails open={open} onClose={onClose} /> : null;
  if (reuse) return details;
  if (failed) return null;
  if (!value) return <Spinner label="Opening…" />;
  return (
    <BandProvider value={value}>{details}</BandProvider>
  );
}
